import { useMemo } from "react";
import { validateScene } from "@/lib/canvas/board-schema";
import { TacticalBoardView } from "@/features/canvas/TacticalBoardView";

/**
 * Read-only chip-sized tactical-board preview for an observation
 * note's thumbnail slot. Ports the legacy `coach-thumb--board`
 * chip variant (`js/coaching/feedback.js:299`) +
 * `tacticalBoardHasContent` gate.
 *
 * Renders only when the raw `tactical_board_json` parses cleanly
 * AND has at least one token or shape. Returns `null` otherwise —
 * the caller falls back to the generic observation glyph. This
 * matches the legacy `hasBoard = isObservation &&
 * tacticalBoardHasContent(...)` ternary.
 *
 * Sized to the 80×48 px tile the rest of the viewer thumbs use
 * (`h-12 w-20`); `TacticalBoardView` derives the height from the
 * pitch aspect so we pass width=80 and let the pitch settle into
 * the tile. Wrapped in a fixed-size box with `overflow-hidden` so
 * a slight aspect mismatch stays clipped to the tile.
 */

export type BoardChipProps = {
  /** Raw `tactical_board_json` from a `CoachingNote`. */
  raw: unknown;
};

export function BoardChip({ raw }: BoardChipProps) {
  const scene = useMemo(() => {
    if (raw == null) return null;
    const result = validateScene(raw);
    if (!result.ok) return null;
    const s = result.value;
    if (s.tokens.length === 0 && s.shapes.length === 0) return null;
    return s;
  }, [raw]);

  if (!scene) return null;

  return (
    <span
      aria-hidden="true"
      data-testid="feedback-board-chip"
      className="block h-12 w-20 shrink-0 overflow-hidden rounded border border-rule/60 bg-surface"
    >
      <TacticalBoardView scene={scene} width={80} />
    </span>
  );
}

/** True if the raw `tactical_board_json` has at least one token
 * or shape. Useful when the caller needs to decide BEFORE
 * mounting the chip (e.g. to render a "⌬ Board" indicator pill
 * alongside the thumbnail). */
export function boardHasContent(raw: unknown): boolean {
  if (raw == null) return false;
  const result = validateScene(raw);
  if (!result.ok) return false;
  return result.value.tokens.length > 0 || result.value.shapes.length > 0;
}
